import type { Runtime, RuntimeCloudDisplayNameUpdate } from '../types'

export const RUNTIME_ALIAS_MAX_CODE_POINTS = 128
// Control and bidi characters are rejected at the display-name boundary.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_RUNTIME_ALIAS_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u206f]/u

export interface RuntimeAliasInput {
  value: string | null
  codePointCount: number
  hasForbiddenCharacters: boolean
  valid: boolean
}

function hasUnpairedSurrogate(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index)
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      if (index + 1 >= value.length) return true
      const next = value.charCodeAt(index + 1)
      if (next < 0xdc00 || next > 0xdfff) return true
      index += 1
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      return true
    }
  }
  return false
}

export function normalizeRuntimeAliasInput(input: string): RuntimeAliasInput {
  const normalized = input.trim().normalize('NFC')
  const codePointCount = Array.from(normalized).length
  const hasForbiddenCharacters = FORBIDDEN_RUNTIME_ALIAS_CHARACTERS.test(normalized)
    || hasUnpairedSurrogate(normalized)
  return {
    value: normalized || null,
    codePointCount,
    hasForbiddenCharacters,
    valid: codePointCount <= RUNTIME_ALIAS_MAX_CODE_POINTS && !hasForbiddenCharacters,
  }
}

export function isNormalizedRuntimeAlias(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const normalized = normalizeRuntimeAliasInput(value)
  return normalized.value === value && normalized.valid
}

export function runtimeDisplayName(id: string, alias?: string, deviceName?: string): string {
  return alias || deviceName || `Runtime ${id.slice(0, 8)}`
}

export function mergeRuntimeCloudDisplayName(
  runtime: Runtime,
  update: RuntimeCloudDisplayNameUpdate,
): Runtime {
  if (runtime.id !== update.runtimeRecordId || runtime.ownershipEpoch !== update.ownershipEpoch
    || (runtime.cloudDisplayNameVersion !== undefined && update.cloudDisplayNameVersion < runtime.cloudDisplayNameVersion)) return runtime
  const alias = update.cloudDisplayName ?? undefined
  return {
    ...runtime,
    cloudDisplayName: runtimeDisplayName(runtime.id, alias, runtime.deviceName),
    cloudDisplayNameOverride: alias,
    cloudDisplayNameVersion: update.cloudDisplayNameVersion,
  }
}
